import CloseIcon from "@mui/icons-material/Close";
import { Avatar } from "@mui/material";
import { createPost } from "apis/PostServiceApi";
import CausesSelector from "components/forms/CausesSelector/CausesSelector";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/types";
import { CreatePostData } from "types";
import LocationFields from "../LocationFields";
import * as S from "./index.styles";

interface PostFormProps {
  onPostCreated?: () => void;
  project_id?: number;
  onClose?: () => void;
}

const PostForm: React.FC<PostFormProps> = ({ onPostCreated, project_id, onClose }) => {
  const user = useSelector((s: RootState) => s.user);
  const [loading, setLoading] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const [showLocation, setShowLocation] = useState(false);

  const initialPostForm = {
    post_caption: "",
    post_image: "",
    post_location: [],
    project_id,
    causeIds: [],
  }

  const [postForm, setPostForm] = useState<CreatePostData>(initialPostForm);

  const handlePostFormChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setPostForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handlePostFormChange(event);
    setImgErr(false);
  }, [handlePostFormChange]);

  const handleImageRemove = useCallback(() => {
    setPostForm(prev => ({ ...prev, post_image: "" }));
    setImgErr(false);
  }, []);

  const handleLocationChange = (index: number, value: string) => {
    setPostForm(prev => {
      const updatedLocations = [...prev.post_location];
      updatedLocations[index] = value;
      return { ...prev, post_location: updatedLocations };
    });
  };

  const toggleLocation = () => {
    setShowLocation(!showLocation);
    if (!showLocation) {
      setPostForm(prev => ({ ...prev, post_location: [] }));
    }
  };

  const publish = async () => {
    if (!postForm.post_caption.trim()) return;
    setLoading(true);
    try {
      await createPost(postForm);
      setPostForm(initialPostForm);
      onPostCreated?.();
      onClose?.();
    } catch (e) {
      console.error("createPost error", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.Wrapper>
      <S.Header>
        <Avatar src={user?.socialAvatarUrl || "/default-profile.png"} />
        <S.Name>{user?.name || "User"}</S.Name>
      </S.Header>

      <S.TextArea
        placeholder="What would you like to share?"
        name="post_caption"
        value={postForm.post_caption}
        onChange={handlePostFormChange}
      />

      <S.ImageArea>
        <S.ImageInput
          placeholder="Enter image URL"
          value={postForm.post_image}
          name="post_image"
          onChange={handleImageChange}
        />
        {postForm.post_image && !imgErr && (
          <S.PreviewWrapper>
            <S.PreviewImg
              src={postForm.post_image}
              onError={() => setImgErr(true)}
            />
            <S.RemoveBtn onClick={handleImageRemove}>
              <CloseIcon fontSize="small" />
            </S.RemoveBtn>
          </S.PreviewWrapper>
        )}
        {imgErr && (
          <span style={{ color: "#ff4444", fontSize: "0.85rem" }}>
            Invalid image URL
          </span>
        )}
      </S.ImageArea>

      <S.LocationToggle isActive={showLocation} onClick={toggleLocation}>
        {showLocation ? "Remove Location" : "Add Location"}
      </S.LocationToggle>

      {showLocation && (
        <LocationFields
          post_location={postForm.post_location}
          onChange={handleLocationChange}
          fields={[
            { type: "country", label: "Country", required: true },
            { type: "state", label: "State/Province", required: true },
            { type: "city", label: "City", required: true },
          ]}
          border={{
            borderColor: "#d0d0d0",
            borderStyle: "solid",
            borderWidth: "0.0625rem",
          }}
        />
      )}

      <CausesSelector
        selectedCauses={postForm.causeIds}
        setSelectedCauses={(causes) => setPostForm(prev => ({ ...prev, causeIds: causes }))}
        border={{
          borderColor: "#d0d0d0",
          borderStyle: "solid",
          borderWidth: "0.0625rem"
        }}
      />
      <S.PublishBtn
        disabled={!postForm.post_caption.trim() || loading || imgErr}
        onClick={publish}
      >
        {loading ? "Posting…" : "Post"}
      </S.PublishBtn>
    </S.Wrapper>
  );
};

export default PostForm; 